// Origens / CDs — PO1-2380 (épico PO1-2374): locais de onde a mercadoria sai
// (os remetentes do embarcador, EmbarcadorRemetente no TMS), usados como origem
// nas notas e nos arquivos para a transportadora. Mesmo molde da tela Contatos
// do embarcador: tabela + modal criar/editar, pelo embarcador escolhido no topo,
// remover com confirmação na tela. O CEP é digitado só com dígitos (ou com o
// hífen) e vai para o backend só com dígitos; o backend resolve o Id na tabela
// CEP e devolve cidade/UF. Nos onChange, o valor do campo é lido ANTES do
// setState (event pooling do React 16).
//
// Permissões: a leitura vem com hub.operacao.pedidos.visualizar; os botões de
// escrita (Adicionar / Editar / Remover) só aparecem com
// hub.operacao.origem.editar (admin Everlog tem bypass, como no menu).
//
// Sprint 638: a tela virou a aba "Origens / CDs" do cadastro do embarcador
// (views/Empresa/Embarcadores). Com `embutido`, não desenha o PageHero e usa o
// `embarcadorId` recebido por prop em vez do embarcador do topo.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Row, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import ShellCard from "components/ShellCard";
import SpecificEmpresaRequiredNotice from "components/SpecificEmpresaRequiredNotice";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useAuth } from "hooks/auth";
import { useCompanies } from "hooks/companies";
import { useToast } from "hooks/toast";
import { listarOrigens, criarOrigem, atualizarOrigem, removerOrigem, mensagemErroOrigem } from "services/origensApi";
import { digitsOnly, formatCep, formatDocument, isValidDocumento, mascaraDocumentoParcial } from "utils/formatDoc";
import { temPermissao } from "../AuditoriaPedido/regras";

export const PERMISSAO_EDITAR_ORIGEM = "hub.operacao.origem.editar";

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };
const labelStyle = { fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted, marginBottom: 4 };
const ORIGEM_VAZIA = { cnpjCpf: "", razaoSocial: "", fantasia: "", inscricaoEstadual: "", cep: "", logradouro: "", numero: "", complemento: "", bairro: "", observacao: "" };

// CEP enquanto digita: só dígitos, até 8, com o hífen depois do quinto.
export function mascaraCepParcial(value) {
  const d = digitsOnly(value).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

function endereco(o) {
  const partes = [o.logradouro, o.numero !== null && o.numero !== undefined && o.numero !== "" ? `nº ${o.numero}` : "", o.complemento].filter(Boolean);
  return partes.join(", ");
}

function cidadeUf(o) {
  return [o.cidade, o.uf].filter(Boolean).join("/");
}

function formDe(o) {
  return {
    cnpjCpf: mascaraDocumentoParcial(o.cnpjCpf || ""),
    razaoSocial: o.razaoSocial || "",
    fantasia: o.fantasia || "",
    inscricaoEstadual: o.inscricaoEstadual || "",
    cep: mascaraCepParcial(o.cep || ""),
    logradouro: o.logradouro || "",
    numero: o.numero === null || o.numero === undefined ? "" : String(o.numero),
    complemento: o.complemento || "",
    bairro: o.bairro || "",
    observacao: o.observacao || "",
  };
}

// Payload do backend: CNPJ/CPF e CEP só dígitos; opcionais vazios vão como null.
export function payloadDoForm(form, embarcadorId) {
  const opcional = (v) => (String(v || "").trim() ? String(v).trim() : null);
  return {
    embarcadorId,
    cnpjCpf: digitsOnly(form.cnpjCpf),
    razaoSocial: form.razaoSocial.trim(),
    fantasia: form.fantasia.trim(),
    inscricaoEstadual: opcional(form.inscricaoEstadual),
    cep: digitsOnly(form.cep),
    logradouro: opcional(form.logradouro),
    numero: String(form.numero || "").trim() ? Number(String(form.numero).trim()) : null,
    complemento: opcional(form.complemento),
    bairro: opcional(form.bairro),
    observacao: opcional(form.observacao),
  };
}

export default function Origens({ embarcadorId: embarcadorIdProp, embarcadorNome, embutido = false } = {}) {
  const { addToast } = useToast();
  const { user } = useAuth();
  const podeEditar = temPermissao(user, PERMISSAO_EDITAR_ORIGEM);
  const { empresa, empresaId, hasEmpresaSelection } = useCompanies();
  const embarcadorId = embutido ? embarcadorIdProp || null : hasEmpresaSelection ? empresaId : null;
  const nomeEmbarcador = embutido ? embarcadorNome : empresa && empresa.label;

  const [origens, setOrigens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [modal, setModal] = useState(null); // { modo: 'novo'|'editar', origem }
  const [form, setForm] = useState(ORIGEM_VAZIA);
  const [remover, setRemover] = useState(null); // origem a confirmar

  const carregar = useCallback(async () => {
    if (!embarcadorId) return;
    setLoading(true);
    setErro("");
    try {
      const lista = await listarOrigens(embarcadorId);
      setOrigens(Array.isArray(lista) ? lista : []);
    } catch (err) {
      setErro(mensagemErroOrigem(err, "Não foi possível carregar as origens."));
    } finally {
      setLoading(false);
    }
  }, [embarcadorId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const campo = (nome) => (e) => {
    const valor = e.target.value;
    setForm((f) => ({ ...f, [nome]: valor }));
  };

  const campoCnpjCpf = (e) => {
    const valor = mascaraDocumentoParcial(e.target.value);
    setForm((f) => ({ ...f, cnpjCpf: valor }));
  };

  const campoCep = (e) => {
    const valor = mascaraCepParcial(e.target.value);
    setForm((f) => ({ ...f, cep: valor }));
  };

  const campoNumero = (e) => {
    const valor = digitsOnly(e.target.value).slice(0, 9);
    setForm((f) => ({ ...f, numero: valor }));
  };

  const abrirNovo = () => {
    setForm(ORIGEM_VAZIA);
    setModal({ modo: "novo", origem: null });
  };

  const abrirEditar = (o) => {
    setForm(formDe(o));
    setModal({ modo: "editar", origem: o });
  };

  const salvar = async () => {
    if (!podeEditar) return;
    setSalvando(true);
    try {
      const dados = payloadDoForm(form, embarcadorId);
      if (modal.modo === "novo") {
        await criarOrigem(dados);
        addToast({ place: "tr", type: "success", title: "Origem criada" });
      } else {
        await atualizarOrigem(modal.origem.id, dados);
        addToast({ place: "tr", type: "success", title: "Origem atualizada" });
      }
      setModal(null);
      await carregar();
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível salvar a origem", description: mensagemErroOrigem(err) });
    } finally {
      setSalvando(false);
    }
  };

  const confirmarRemocao = async () => {
    if (!podeEditar) return;
    setSalvando(true);
    try {
      await removerOrigem(remover.id, embarcadorId);
      addToast({ place: "tr", type: "success", title: "Origem removida" });
      setRemover(null);
      await carregar();
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível remover a origem", description: mensagemErroOrigem(err) });
    } finally {
      setSalvando(false);
    }
  };

  const documentoValido = isValidDocumento(form.cnpjCpf);
  const cepValido = digitsOnly(form.cep).length === 8;
  const podeSalvar = documentoValido && cepValido && form.razaoSocial.trim() && form.fantasia.trim();

  const Raiz = embutido ? React.Fragment : "div";
  return (
    <Raiz {...(embutido ? {} : { className: "content" })}>
      {embutido ? null : (
        <PageHero
          eyebrow="Configurações"
          title="Origens / CDs"
          description="Locais de onde a mercadoria sai: os remetentes do embarcador, usados como origem nas notas e nos arquivos para a transportadora."
          actions={embarcadorId ? <PageHeroRefreshButton onClick={carregar} loading={loading} /> : null}
        />
      )}

      {!embarcadorId ? (
        <div style={{ marginTop: "1.25rem" }}>
          <SpecificEmpresaRequiredNotice message="As origens são por embarcador: escolha um embarcador específico no topo da página." />
        </div>
      ) : (
        <ShellCard
          title="Origens"
          description={
            <span style={mutedStyle}>
              Locais de onde a mercadoria sai de <strong style={{ color: AUTOPILOT_COLORS.title }}>{nomeEmbarcador || `embarcador ${embarcadorId}`}</strong>, usados como origem nas notas e nos arquivos para a transportadora.
            </span>
          }
          action={
            podeEditar ? (
              <Button size="sm" color="info" onClick={abrirNovo}>
                + Adicionar
              </Button>
            ) : null
          }
        >
          {erro ? <div style={{ ...stateBoxStyle, color: AUTOPILOT_COLORS.statusDanger }}>{erro}</div> : null}
          {loading && origens.length === 0 ? (
            <div style={stateBoxStyle}>
              <Spinner size="sm" />
              <span>Carregando…</span>
            </div>
          ) : null}
          {!loading && !erro && origens.length === 0 ? <div style={stateBoxStyle}>Nenhuma origem para este embarcador.</div> : null}
          {origens.length > 0 ? (
            <Table size="sm" responsive hover className="hub-thead-accent">
              <thead className="text-primary">
                <tr>
                  <th>Fantasia</th>
                  <th>Razão social</th>
                  <th>CNPJ/CPF</th>
                  <th>IE</th>
                  <th>Endereço</th>
                  <th>Bairro</th>
                  <th>Cidade/UF</th>
                  <th>CEP</th>
                  {podeEditar ? <th aria-label="Ações" /> : null}
                </tr>
              </thead>
              <tbody>
                {origens.map((o) => (
                  <tr key={o.id}>
                    <td style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{o.fantasia}</td>
                    <td>{o.razaoSocial || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDocument(o.cnpjCpf)}</td>
                    <td>{o.inscricaoEstadual || "—"}</td>
                    <td>{endereco(o) || "—"}</td>
                    <td>{o.bairro || "—"}</td>
                    <td>{cidadeUf(o) || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{o.cep ? formatCep(o.cep) : "—"}</td>
                    {podeEditar ? (
                      <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                        <Button size="sm" color="info" outline onClick={() => abrirEditar(o)}>
                          Editar
                        </Button>{" "}
                        <Button size="sm" color="danger" outline onClick={() => setRemover(o)} aria-label={`Remover ${o.fantasia}`}>
                          Remover
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </ShellCard>
      )}

      <Modal isOpen={!!modal} toggle={salvando ? undefined : () => setModal(null)} modalClassName="modal-align-top" size="lg" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.75rem" }}>
            {modal && modal.modo === "novo" ? "Nova origem" : "Editar origem"}
          </div>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label style={labelStyle} for="origem-cnpjCpf">CNPJ/CPF *</Label>
                <Input id="origem-cnpjCpf" value={form.cnpjCpf} maxLength={18} onChange={campoCnpjCpf} invalid={!!form.cnpjCpf && !documentoValido} placeholder="00.000.000/0000-00" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label style={labelStyle} for="origem-fantasia">Nome fantasia *</Label>
                <Input id="origem-fantasia" value={form.fantasia} maxLength={100} onChange={campo("fantasia")} />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label style={labelStyle} for="origem-inscricaoEstadual">Inscrição estadual</Label>
                <Input id="origem-inscricaoEstadual" value={form.inscricaoEstadual} maxLength={20} onChange={campo("inscricaoEstadual")} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label style={labelStyle} for="origem-razaoSocial">Razão social *</Label>
            <Input id="origem-razaoSocial" value={form.razaoSocial} maxLength={100} onChange={campo("razaoSocial")} />
          </FormGroup>
          <Row>
            <Col md="3">
              <FormGroup>
                <Label style={labelStyle} for="origem-cep">CEP *</Label>
                <Input id="origem-cep" value={form.cep} maxLength={9} inputMode="numeric" onChange={campoCep} placeholder="00000-000" />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label style={labelStyle} for="origem-logradouro">Logradouro</Label>
                <Input id="origem-logradouro" value={form.logradouro} maxLength={255} onChange={campo("logradouro")} />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label style={labelStyle} for="origem-numero">Número</Label>
                <Input id="origem-numero" value={form.numero} maxLength={9} inputMode="numeric" onChange={campoNumero} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label style={labelStyle} for="origem-complemento">Complemento</Label>
                <Input id="origem-complemento" value={form.complemento} maxLength={255} onChange={campo("complemento")} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label style={labelStyle} for="origem-bairro">Bairro</Label>
                <Input id="origem-bairro" value={form.bairro} maxLength={60} onChange={campo("bairro")} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label style={labelStyle} for="origem-observacao">Observação</Label>
            <Input id="origem-observacao" type="textarea" rows={2} value={form.observacao} maxLength={255} onChange={campo("observacao")} />
          </FormGroup>
          <div style={mutedStyle}>Cidade e UF vêm do CEP informado.</div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button color="info" onClick={salvar} disabled={salvando || !podeSalvar}>
            {salvando ? <Spinner size="sm" /> : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!remover} toggle={salvando ? undefined : () => setRemover(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.5rem" }}>Remover origem</div>
          {remover ? (
            <div>
              <strong>{remover.fantasia}</strong> ({formatDocument(remover.cnpjCpf)}) deixa de estar disponível como origem. Origens com nota fiscal ou CT-e vinculado não podem ser removidas. Esta ação não pode ser desfeita.
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setRemover(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button color="danger" onClick={confirmarRemocao} disabled={salvando}>
            {salvando ? <Spinner size="sm" /> : "Remover"}
          </Button>
        </ModalFooter>
      </Modal>
    </Raiz>
  );
}
